import { z } from 'zod';

import type {
  GetMaterialSummaryReportRequest,
  GetMaterialSummaryReportResponse,
  MaterialSummaryReportRow,
  MaterialSummaryReportTotal,
  MaterialSummaryReportValues,
} from './material-summary.dto.js';
import {
  IsoDateSchema,
  IsoDateTimeSchema,
  RcScopeTypeSchema,
} from '../shipments/shipments-types.js';
import { ReportableWeightLbSchema } from '../reportable-weight.schema.js';

const nonNegativeIntSchema = z.number().int().min(0);
const trimmedStringSchema = z.string().trim().min(1);

const validateLocalDateOrder = (value: unknown, ctx: z.RefinementCtx): void => {
  if (!value || typeof value !== 'object') {
    return;
  }

  const dateStart = (value as { dateStart?: string }).dateStart;
  const dateEnd = (value as { dateEnd?: string }).dateEnd;
  if (typeof dateStart !== 'string' || typeof dateEnd !== 'string') {
    return;
  }

  if (dateStart > dateEnd) {
    ctx.addIssue({
      code: 'custom',
      message: 'dateStart must be on or before dateEnd.',
      path: ['dateEnd'],
    });
  }
};

export const GetMaterialSummaryReportRequestSchema: z.ZodType<GetMaterialSummaryReportRequest> = z
  .object({
    companyId: z.uuid(),
    rcScopeType: RcScopeTypeSchema,
    rcScopeId: z.uuid(),
    dateStart: IsoDateSchema,
    dateEnd: IsoDateSchema,
    materialId: z.uuid().nullable().optional(),
  })
  .strict()
  .superRefine(validateLocalDateOrder);

const MaterialSummaryReportValuesSchemaBase = z
  .object({
    swLb: ReportableWeightLbSchema,
    scsCount: nonNegativeIntSchema,
    sclCount: nonNegativeIntSchema,
    sc25Count: nonNegativeIntSchema,
    spWeightLb: ReportableWeightLbSchema,
    refundValueCents: nonNegativeIntSchema,
    redemptionWeightLb: ReportableWeightLbSchema,
    totalWeightLb: ReportableWeightLbSchema,
    discountCents: nonNegativeIntSchema,
    hasDiscountAdjustment: z.boolean(),
  })
  .strict();

export const MaterialSummaryReportValuesSchema: z.ZodType<MaterialSummaryReportValues> =
  MaterialSummaryReportValuesSchemaBase;

export const MaterialSummaryReportRowSchema: z.ZodType<MaterialSummaryReportRow> =
  MaterialSummaryReportValuesSchemaBase.extend({
    date: IsoDateSchema,
    materialId: z.uuid(),
    materialName: trimmedStringSchema,
  }).strict();

export const MaterialSummaryReportTotalSchema: z.ZodType<MaterialSummaryReportTotal> =
  MaterialSummaryReportValuesSchemaBase.extend({
    materialId: z.uuid().nullable(),
    materialName: trimmedStringSchema,
  }).strict();

export const GetMaterialSummaryReportResponseSchema: z.ZodType<GetMaterialSummaryReportResponse> = z
  .object({
    companyId: z.uuid(),
    rcScopeType: RcScopeTypeSchema,
    rcScopeId: z.uuid(),
    timeZone: trimmedStringSchema,
    dateStart: IsoDateSchema,
    dateEnd: IsoDateSchema,
    materialId: z.uuid().nullable(),
    hasDiscountAdjustedDays: z.boolean(),
    rows: z.array(MaterialSummaryReportRowSchema),
    materialTotals: z.array(MaterialSummaryReportTotalSchema),
    rangeTotal: MaterialSummaryReportTotalSchema,
    generatedAt: IsoDateTimeSchema,
  })
  .strict()
  .superRefine(validateLocalDateOrder);
